import React, { useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaTimes, FaExchangeAlt, FaExclamationTriangle,
  FaSpinner, FaCheck, FaArrowRight, FaInfoCircle,
  FaCalendarAlt, FaAlignLeft,
} from 'react-icons/fa';
import './ModalConversaoConta.css';

/* ─── helpers ─────────────────────────────────────────────────────────── */
const fmtCur = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

/* ─── config por tipo ─────────────────────────────────────────────────── */
const TIPO_CFG = {
  ap_to_ar: {
    titulo:    'Converter em Conta a Receber',
    de:        'Conta a Pagar',
    para:      'Conta a Receber',
    corDe:     '#e11d48',
    corPara:   '#059669',
    bgDe:      '#fff1f2',
    bgPara:    '#ecfdf5',
    endpoint:  (id) => `/api/contas-a-pagar/${id}/converter-para-receber`,
    alerta: [
      'A conta a pagar será <strong>encerrada (cancelada)</strong> e um novo crédito a receber será criado.',
      'Use quando o fornecedor emite nota de crédito, devolução de mercadoria ou reembolso de pagamento em duplicidade.',
      'A operação é <strong>auditada e irreversível</strong> sem intervenção do Admin Geral.',
    ],
    aviso_fiscal:
      'Verifique se há nota fiscal associada antes de converter. A mudança pode exigir emissão de nota de crédito pelo fornecedor.',
  },
  ar_to_ap: {
    titulo:    'Converter em Conta a Pagar',
    de:        'Conta a Receber',
    para:      'Conta a Pagar',
    corDe:     '#059669',
    corPara:   '#e11d48',
    bgDe:      '#ecfdf5',
    bgPara:    '#fff1f2',
    endpoint:  (id) => `/api/contas-a-receber/${id}/converter-para-pagar`,
    alerta: [
      'A conta a receber será <strong>encerrada (cancelada)</strong> e um novo compromisso a pagar será criado.',
      'Use quando há devolução ao cliente, reembolso por cobrança indevida ou crédito concedido.',
      'A operação é <strong>auditada e irreversível</strong> sem intervenção do Admin Geral.',
    ],
    aviso_fiscal:
      'Verifique se há nota fiscal associada. Uma NF de devolução ou estorno pode ser necessária para regularização tributária.',
  },
};

/* ─── Component ──────────────────────────────────────────────────────── */
export default function ModalConversaoConta({ conta, tipo, onClose, onSuccess }) {
  const cfg = TIPO_CFG[tipo];

  const [justificativa,   setJustificativa]   = useState('');
  const [dataVencimento,  setDataVencimento]  = useState(conta?.data_vencimento
    ? String(conta.data_vencimento).split('T')[0]
    : todayISO());
  const [confirmado,      setConfirmado]      = useState(false);
  const [submitting,      setSubmitting]      = useState(false);

  const minChars       = 30;
  const charCount      = justificativa.trim().length;
  const justOk         = charCount >= minChars;
  const canSubmit      = justOk && confirmado && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(cfg.endpoint(conta.id), {
        justificativa:  justificativa.trim(),
        data_vencimento: dataVencimento,
      });
      toast.success(data.message || 'Conta convertida com sucesso!');
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao converter conta.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!conta || !cfg) return null;

  return (
    <div className="mcv-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mcv-modal">

        {/* ── Header ── */}
        <div className="mcv-header">
          <div className="mcv-header-icon">
            <FaExchangeAlt />
          </div>
          <div>
            <span className="mcv-eyebrow">Operação Contábil Sensível</span>
            <h2 className="mcv-title">{cfg.titulo}</h2>
          </div>
          <button className="mcv-close" onClick={onClose}><FaTimes /></button>
        </div>

        {/* ── Fluxo visual de conversão ── */}
        <div className="mcv-flow">
          <div className="mcv-flow-box" style={{ background: cfg.bgDe, borderColor: cfg.corDe }}>
            <span className="mcv-flow-label" style={{ color: cfg.corDe }}>{cfg.de}</span>
            <strong className="mcv-flow-id">#{conta.id}</strong>
          </div>
          <FaArrowRight className="mcv-flow-arrow" />
          <div className="mcv-flow-box" style={{ background: cfg.bgPara, borderColor: cfg.corPara }}>
            <span className="mcv-flow-label" style={{ color: cfg.corPara }}>{cfg.para}</span>
            <strong className="mcv-flow-id">Nova</strong>
          </div>
        </div>

        {/* ── Dados da conta original ── */}
        <div className="mcv-account-card">
          <div className="mcv-account-row">
            <span className="mcv-account-label">Descrição</span>
            <span className="mcv-account-val">{conta.descricao}</span>
          </div>
          <div className="mcv-account-row">
            <span className="mcv-account-label">Valor</span>
            <strong className="mcv-account-val mcv-account-val--money">{fmtCur(conta.valor)}</strong>
          </div>
          <div className="mcv-account-row">
            <span className="mcv-account-label">Vencimento Original</span>
            <span className="mcv-account-val">{fmtDate(conta.data_vencimento)}</span>
          </div>
          <div className="mcv-account-row">
            <span className="mcv-account-label">Status Atual</span>
            <span className="mcv-account-val mcv-status-tag">{conta.status}</span>
          </div>
        </div>

        {/* ── Alertas contábeis ── */}
        <div className="mcv-alerts">
          {cfg.alerta.map((txt, i) => (
            <div key={i} className="mcv-alert-row">
              <FaExclamationTriangle className="mcv-alert-icon" />
              <span dangerouslySetInnerHTML={{ __html: txt }} />
            </div>
          ))}
        </div>

        {/* ── Aviso fiscal ── */}
        <div className="mcv-fiscal">
          <FaInfoCircle className="mcv-fiscal-icon" />
          <span>{cfg.aviso_fiscal}</span>
        </div>

        {/* ── Formulário ── */}
        <div className="mcv-form">

          {/* Novo vencimento */}
          <div className="mcv-field">
            <label>
              <FaCalendarAlt /> Vencimento da nova conta
            </label>
            <input
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              min={todayISO()}
            />
          </div>

          {/* Justificativa */}
          <div className="mcv-field">
            <label>
              <FaAlignLeft /> Justificativa contábil
              <span className="mcv-req">*</span>
              <span className="mcv-char-count" data-ok={justOk}>
                {charCount}/{minChars} mín.
              </span>
            </label>
            <textarea
              placeholder="Descreva detalhadamente o motivo da conversão: nota de crédito nº, acordo firmado em, devolutiva referente a NF-e nº…"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <span className="mcv-field-hint">
              Obrigatório · mínimo {minChars} caracteres · ficará registrado no log de auditoria
            </span>
          </div>

          {/* Confirmação explícita */}
          <label className="mcv-confirm-check">
            <input
              type="checkbox"
              checked={confirmado}
              onChange={(e) => setConfirmado(e.target.checked)}
            />
            <span>
              Confirmo que li os avisos acima, que esta conversão é necessária e que
              estou ciente das implicações contábeis e fiscais.
            </span>
          </label>
        </div>

        {/* ── Footer ── */}
        <div className="mcv-footer">
          <button className="mcv-btn-cancel" onClick={onClose} disabled={submitting}>
            <FaTimes /> Cancelar
          </button>
          <button
            className="mcv-btn-confirm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? <><FaSpinner className="mcv-spin" /> Convertendo…</>
              : <><FaCheck /> Confirmar Conversão</>}
          </button>
        </div>

      </div>
    </div>
  );
}